import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import MonitoringFilters, {
  MonitoringFilterScope,
  useMonitoringFilterQuery,
} from '../../components/workforceMonitoring/MonitoringFilters';
import SortableHeader from '../../components/workforceMonitoring/SortableHeader';
import { SortDir, sortRows, toggleSort } from '../../utils/tableSort';

const DISPLAY_TZ = 'America/Chicago';

function formatClockTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return moment(value).tz(DISPLAY_TZ).format('h:mm A');
}

function formatDurationCell(seconds: number | null | undefined, label?: string | null, emptyDash = false): string {
  if (emptyDash && (seconds == null || seconds <= 0)) return '—';
  if (label) return label;
  if (seconds == null || seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function segmentTimelineLabel(segment: any): string {
  const start = formatClockTime(segment.start_time);
  const end = segment.end_time ? formatClockTime(segment.end_time) : 'now';
  const campaign = segment.campaign_name ? ` – ${segment.campaign_name}` : '';
  const kind = segment.diagnostic ? `${segment.session_type} (diagnostic)` : segment.session_type;
  return `${start}–${end}  ${kind}${campaign}  (${segment.duration_label || '0m'})`;
}

export interface SessionsPageProps {
  scope?: MonitoringFilterScope;
  lockedDeviceId?: string;
}

const SessionsPage: React.FC<SessionsPageProps> = ({
  scope = 'global',
  lockedDeviceId,
}) => {
  const [filters, setFilters] = useMonitoringFilterQuery(scope, lockedDeviceId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('clock_in');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringSessions', scope, filters],
    queryFn: () => apiClient.monitoring.getSessions({
      date: filters.date,
      employee_id: filters.employeeId || undefined,
      device_id: filters.deviceId || undefined,
      campaign_id: filters.campaignId || undefined,
      status: filters.status || undefined,
    }),
    refetchInterval: 30_000,
  });

  const rows = sortRows(data?.data?.data || [], sortBy, sortDir, (row: any, key) => {
    if (key === 'clock_in' || key === 'clock_out') return row[key] ? new Date(row[key]).getTime() : 0;
    return row[key];
  });
  const showComputer = scope === 'global';
  const colSpan = showComputer ? 12 : 11;
  const emptyMessage = scope === 'computer'
    ? 'No work sessions recorded for this computer.'
    : 'No work sessions recorded.';
  const onSort = (column: string) => {
    const next = toggleSort(sortBy, sortDir, column);
    setSortBy(next.key);
    setSortDir(next.dir);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {scope === 'computer'
          ? 'Work session history for this computer — clock in/out, duration, and status (newest clock-in first).'
          : 'Session history for all computers — who worked where, for how long, and whether the session is still active.'}
      </Typography>
      <MonitoringFilters
        scope={scope}
        value={filters}
        onChange={setFilters}
        lockedDeviceId={lockedDeviceId}
        showStatus
      />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <SortableHeader label="Employee" column="employee_name" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              {showComputer && (
                <SortableHeader label="Computer" column="computer" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              )}
              <SortableHeader label="Campaign" column="campaign" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Clock In" column="clock_in" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Clock Out" column="clock_out" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Work" column="work_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Idle / Locked" column="locked_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Break" column="break_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Bathroom" column="bathroom_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Lunch" column="lunch_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Status" column="status" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colSpan}>Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row: any) => {
              const notClockedIn = row.status === 'NOT CLOCKED IN' || !row.has_authoritative_clock_in;
              return (
                <React.Fragment key={row.employee_id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpandedId(expandedId === row.employee_id ? null : row.employee_id)}>
                        {expandedId === row.employee_id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.employee_name}</TableCell>
                    {showComputer && <TableCell>{row.computer || '—'}</TableCell>}
                    <TableCell>{row.campaign || '—'}</TableCell>
                    <TableCell>{formatClockTime(row.clock_in)}</TableCell>
                    <TableCell>{formatClockTime(row.clock_out)}</TableCell>
                    <TableCell>{formatDurationCell(row.work_seconds, row.work_label)}</TableCell>
                    <TableCell>{formatDurationCell(row.locked_seconds ?? row.idle_seconds, row.idle_locked_label, notClockedIn)}</TableCell>
                    <TableCell>{formatDurationCell(row.break_seconds, row.break_label, notClockedIn)}</TableCell>
                    <TableCell>{formatDurationCell(row.bathroom_seconds, row.bathroom_label, notClockedIn)}</TableCell>
                    <TableCell>{formatDurationCell(row.lunch_seconds, row.lunch_label, notClockedIn)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={notClockedIn ? 'default' : (row.status === 'WORKING' || row.status === 'OPEN' ? 'success' : 'default')}
                        label={row.status}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={colSpan} sx={{ py: 0 }}>
                      <Collapse in={expandedId === row.employee_id}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          {row.clock_in && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                              {formatClockTime(row.clock_in)}  Clock In
                            </Typography>
                          )}
                          {(row.segments || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {notClockedIn
                                ? 'No TEKS work session yet. Authentication / agent / Windows events are not Clock In.'
                                : 'No segment details.'}
                            </Typography>
                          ) : (
                            (row.segments || []).map((segment: any) => (
                              <Typography
                                key={`${segment.session_type}-${segment.start_time}-${segment.diagnostic ? 'd' : 'w'}`}
                                variant="body2"
                                sx={{ fontFamily: 'monospace', opacity: segment.diagnostic ? 0.7 : 1 }}
                              >
                                {segmentTimelineLabel(segment)}
                              </Typography>
                            ))
                          )}
                          {row.clock_out && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                              {formatClockTime(row.clock_out)}  Clock Out
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SessionsPage;
